import { Stack, Typography } from "@mui/material"
import { CSSProperties } from "react"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import type { ResourceTenantSegment } from "./tenantUsageViewModel"

const useStyles = makeStyles()((theme) => ({
    row: {
        padding: ".25rem 0",
        transition: "opacity .15s ease",
    },
    rowDimmed: {
        opacity: 0.4,
    },
    swatch: {
        flex: "0 0 auto",
        width: ".625rem",
        height: ".625rem",
        borderRadius: ".125rem",
        backgroundColor: "var(--sw-color)",
    },
    name: {
        flexGrow: 1,
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[600],
    },
    percent: {
        flex: "0 0 auto",
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[500],
    },
}))

type ResourceUsageDetailProps = Readonly<{
    segments: ReadonlyArray<ResourceTenantSegment>
    hoveredKey: string | null
    onHoverSegment: (key: string | null) => void
}>

export function ResourceUsageDetail({ segments, hoveredKey, onHoverSegment }: ResourceUsageDetailProps) {
    const { classes, cx } = useStyles()

    return (
        <Stack gap=".25rem" paddingTop=".75rem">
            {segments.map((segment) => {
                // CSS custom property for the swatch color (data-driven design token).
                const swatchStyle = { "--sw-color": segment.color } as CSSProperties
                const dimmed = hoveredKey != null && hoveredKey !== segment.key

                return (
                    <Stack
                        key={segment.key}
                        direction="row"
                        alignItems="center"
                        gap=".625rem"
                        className={cx(classes.row, dimmed && classes.rowDimmed)}
                        onMouseEnter={() => onHoverSegment(segment.key)}
                        onMouseLeave={() => onHoverSegment(null)}
                    >
                        <span className={classes.swatch} style={swatchStyle} />
                        {segment.isOther ? (
                            <LocalizedStrict
                                id="service-center-usage-per-tenant-detail-other-tenants"
                                vars={{ count: segment.aggregatedCount }}
                            >
                                <Typography variant="caption" className={classes.name}>
                                    {segment.aggregatedCount} other tenants
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <Typography variant="caption" className={classes.name}>
                                {segment.name}
                            </Typography>
                        )}
                        <Typography variant="caption" className={classes.percent}>
                            {segment.usageRate}%
                        </Typography>
                    </Stack>
                )
            })}
        </Stack>
    )
}
